import type { EntitySummary, ExplorerSortConfig, ExplorerSortRule } from '../types/entities';

const germanCollator = new Intl.Collator('de-DE', { numeric: true, sensitivity: 'base' });

export function readExplorerSortConfig(value: unknown): ExplorerSortConfig | null {
  if (!value || typeof value !== 'object') return null;
  const config = value as { primary?: unknown; secondary?: unknown };
  const primary = readRule(config.primary);
  if (!primary) return null;
  return { primary, secondary: readRule(config.secondary) };
}

function readRule(value: unknown): ExplorerSortRule | null {
  if (!value || typeof value !== 'object') return null;
  const rule = value as { key?: unknown; direction?: unknown };
  if (typeof rule.key !== 'string' || !rule.key) return null;
  if (!['asc', 'desc'].includes(String(rule.direction))) return null;
  return { key: rule.key, direction: rule.direction as ExplorerSortRule['direction'] };
}

function isEmpty(value: unknown) {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

function comparableValue(value: unknown): number | string {
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) return value.getTime();
  if (Array.isArray(value)) return value.map(comparableValue).join(' · ');
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['chronologyKey', 'sortKey', 'start', 'label', 'value']) {
      if (!isEmpty(record[key])) return comparableValue(record[key]);
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function compareValues(left: unknown, right: unknown) {
  const leftEmpty = isEmpty(left); const rightEmpty = isEmpty(right);
  if (leftEmpty || rightEmpty) return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1;
  const a = comparableValue(left); const b = comparableValue(right);
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return germanCollator.compare(String(a), String(b));
}

export function sortEntitiesByExplorerRules(
  items: EntitySummary[],
  config: ExplorerSortConfig,
  reversed: boolean,
  valueFor: (entity: EntitySummary, key: string) => unknown
) {
  const direction = (rule: ExplorerSortRule) => (rule.direction === 'desc' ? -1 : 1) * (reversed ? -1 : 1);
  const compareRule = (left: EntitySummary, right: EntitySummary, rule: ExplorerSortRule) => {
    const leftValue = valueFor(left, rule.key); const rightValue = valueFor(right, rule.key);
    const leftEmpty = isEmpty(leftValue); const rightEmpty = isEmpty(rightValue);
    if (leftEmpty || rightEmpty) return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1;
    return compareValues(leftValue, rightValue) * direction(rule);
  };
  return [...items].sort((left, right) => {
    const primary = compareRule(left, right, config.primary);
    if (primary) return primary;
    const secondary = config.secondary ? compareRule(left, right, config.secondary) : 0;
    if (secondary) return secondary;
    return germanCollator.compare(left.title, right.title) * (reversed ? -1 : 1) || left.id.localeCompare(right.id);
  });
}
